import Features from "@components/Features"
import FrequentlyAskedQuestions from "@components/FrequentlyAskedQuestions"
import LayoutPlansPage from "@components/LayoutPlansPage"
import LimitedTimeOfferDialog from "@components/LimitedTimeOfferDialog"
import MoneyBackGuarantee from "@components/MoneyBackGuarantee"
import Plans from "@components/Plans"
import Trust from "@components/Trust"
import ErrorPage from "@pages/ErrorPage"
import LoadingPage from "@pages/LoadingPage"
import { Suspense, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Await, useLoaderData } from "react-router-dom"
import {
  GetPricesData,
  GetPricesLoaderResult,
} from "src/loaders/getPricesLoader"
import { Plan } from "src/types"

export default function PlansPage() {
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [selectedPlan, setSelectedPlan] = useState<Plan>("yearly")
  const [timeLeft, setTimeLeft] = useState("00:00")

  const data = useLoaderData() as GetPricesLoaderResult
  const { t } = useTranslation()

  useEffect(() => {
    // Show dialog after 30 seconds regardless of preselected plan
    const dialogTimeout = setTimeout(() => {
      setIsDialogOpen(true)
    }, 30000)

    const startMinutes = 9
    setTimeLeft("09:00")

    const endTime = new Date().getTime() + startMinutes * 60 * 1000

    const timer = setInterval(() => {
      const now = new Date().getTime()
      const distance = endTime - now

      if (distance <= 0) {
        clearInterval(timer)
        setTimeLeft("00:00")
        return
      }

      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60))
      const seconds = Math.floor((distance % (1000 * 60)) / 1000)

      setTimeLeft(
        `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`,
      )
    }, 1000)

    return () => {
      clearInterval(timer)
      clearTimeout(dialogTimeout)
    }
  }, [])

  if (isSubmitting) return <LoadingPage />

  return (
    <Suspense fallback={<LoadingPage />}>
      <Await errorElement={<ErrorPage />} resolve={data.getPricesData}>
        {(getPricesData: GetPricesData) => (
          <LayoutPlansPage
            currency={getPricesData.currency}
            fullPrice={
              selectedPlan === "weekly"
                ? getPricesData.weeklyPrice
                : getPricesData.yearlyPrice
            }
            isSubmitting={isSubmitting}
            isWeekly={selectedPlan === "weekly"}
            priceId={
              selectedPlan === "weekly"
                ? getPricesData.weeklyIntroPrice?.id
                : getPricesData.yearlyIntroPrice?.id
            }
            setIsSubmitting={setIsSubmitting}
            timeLeft={timeLeft}
          >
            <div className="space-y-3 text-center">
              <h1 className="custom-heading">
                {t("pages.onboarding.plans.heading")}
              </h1>

              <h2 className="custom-subheading">
                {t("pages.onboarding.plans.subheading")}
              </h2>
            </div>

            <section>
              <Plans
                currency={getPricesData.currency}
                isSubmitting={isSubmitting}
                setIsSubmitting={setIsSubmitting}
                timeLeft={timeLeft}
                weeklyIntroPrice={getPricesData.weeklyIntroPrice}
                weeklyPrice={getPricesData.weeklyPrice}
                yearlyIntroPrice={getPricesData.yearlyIntroPrice}
                yearlyPrice={getPricesData.yearlyPrice}
                selectedPlan={selectedPlan}
                onPlanChange={setSelectedPlan}
              />
            </section>

            <section>
              <Trust />
            </section>

            <section>
              <Features />
            </section>

            <section>
              <MoneyBackGuarantee />
            </section>

            <section>
              <FrequentlyAskedQuestions />
            </section>

            <section>
              <Plans
                currency={getPricesData.currency}
                isSubmitting={isSubmitting}
                setIsSubmitting={setIsSubmitting}
                timeLeft={timeLeft}
                weeklyIntroPrice={getPricesData.weeklyIntroPrice}
                weeklyPrice={getPricesData.weeklyPrice}
                yearlyIntroPrice={getPricesData.yearlyIntroPrice}
                yearlyPrice={getPricesData.yearlyPrice}
                selectedPlan={selectedPlan}
                onPlanChange={setSelectedPlan}
              />
            </section>

            <LimitedTimeOfferDialog
              currency={getPricesData.currency}
              isSubmitting={isSubmitting}
              onClose={() => setIsDialogOpen(false)}
              open={isDialogOpen}
              plan={selectedPlan}
              setIsSubmitting={setIsSubmitting}
              stripeIntroPrice={
                selectedPlan === "weekly"
                  ? getPricesData.weeklyIntroPrice
                  : getPricesData.yearlyIntroPrice
              }
              stripePrice={
                selectedPlan === "weekly"
                  ? getPricesData.weeklyPrice
                  : getPricesData.yearlyPrice
              }
            />
          </LayoutPlansPage>
        )}
      </Await>
    </Suspense>
  )
}
